import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Crown, Lock, CheckCircle2, Trophy, Video } from 'lucide-react';
import { Progress } from './ui/progress';

const GameDashboard = ({ stages, userProgress, onStageSelect, onNavigateToVideos }) => {
  // Handle null userProgress safely
  const completedCount = userProgress?.completedStages?.length || 0;
  const totalStages = stages.length;
  const progressPercentage = (completedCount / totalStages) * 100;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center mb-4">
            <Crown className="w-16 h-16 text-purple-700" strokeWidth={1.5} />
          </div>
          <h1 className="text-5xl font-bold mb-3 bg-gradient-to-r from-purple-700 via-blue-700 to-purple-700 bg-clip-text text-transparent">
            Beneficiaries Blueprint
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
            Master the Reclaimation Process pioneered by Empress Ninti El Bey
          </p>
          <p className="text-base text-slate-500 mt-2">
            Your path to freedom and reclaiming your birth rights
          </p>
          
          {/* Videos Button */}
          <div className="mt-6">
            <Button
              onClick={onNavigateToVideos}
              className="bg-purple-700 hover:bg-purple-800 text-white px-8 py-6 text-lg transition-colors"
            >
              <Video className="w-5 h-5 mr-2" />
              Watch Instructional Videos
            </Button>
          </div>
        </div>

        {/* Progress Overview */}
        <Card className="mb-12 border-2 border-purple-200 shadow-lg">
          <CardHeader className="bg-gradient-to-r from-purple-50 to-blue-50">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-2xl text-purple-900">Your Journey Progress</CardTitle>
                <CardDescription className="text-slate-600 mt-1">
                  {completedCount} of {totalStages} stages completed
                </CardDescription>
              </div>
              <Trophy className="w-12 h-12 text-yellow-600" />
            </div>
          </CardHeader>
          <CardContent className="pt-6">
            <Progress value={progressPercentage} className="h-3 mb-3" />
            <p className="text-sm text-slate-600 text-center">
              {progressPercentage === 100 ? 'Journey Complete! 🎉' : `${Math.round(progressPercentage)}% Complete - Keep going!`}
            </p>
          </CardContent>
        </Card>

        {/* Stage Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {stages.map((stage, index) => {
            const isLocked = !stage.unlocked && !(userProgress?.completedStages || []).includes(stage.id - 1);
            const isCompleted = (userProgress?.completedStages || []).includes(stage.id);
            const isCurrent = userProgress?.currentStage === stage.id;

            return (
              <Card
                key={stage.id}
                className={`transition-all duration-300 hover:shadow-xl cursor-pointer border-2 ${
                  isCompleted
                    ? 'border-green-300 bg-gradient-to-br from-green-50 to-emerald-50'
                    : isCurrent
                    ? 'border-purple-400 bg-gradient-to-br from-purple-50 to-blue-50 shadow-lg transform scale-105'
                    : isLocked
                    ? 'border-slate-300 bg-slate-50 opacity-60'
                    : 'border-slate-300 hover:border-purple-300'
                }`}
                onClick={() => !isLocked && onStageSelect(stage)}
              >
                <CardHeader>
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div
                        className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg ${
                          isCompleted
                            ? 'bg-green-600 text-white'
                            : isCurrent
                            ? 'bg-purple-700 text-white'
                            : isLocked
                            ? 'bg-slate-400 text-white'
                            : 'bg-blue-700 text-white'
                        }`}
                      >
                        {stage.id}
                      </div>
                    </div>
                    {isLocked && <Lock className="w-5 h-5 text-slate-500" />}
                    {isCompleted && <CheckCircle2 className="w-6 h-6 text-green-600" />}
                  </div>
                  <CardTitle className="text-xl mb-2 text-slate-900">{stage.title}</CardTitle>
                  <CardDescription className="text-slate-600 leading-relaxed">
                    {stage.subtitle}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button
                    disabled={isLocked}
                    className={`w-full ${
                      isCompleted
                        ? 'bg-green-600 hover:bg-green-700'
                        : isCurrent
                        ? 'bg-purple-700 hover:bg-purple-800'
                        : 'bg-blue-700 hover:bg-blue-800'
                    } text-white transition-colors`}
                  >
                    {isCompleted ? 'Review Stage' : isLocked ? 'Locked' : 'Start Stage'}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Footer */}
        <div className="mt-16 text-center border-t-2 border-slate-200 pt-8">
          <p className="text-slate-600 mb-2">
            Knowledge Base Reference
          </p>
          <a
            href="https://knowledgebase.live"
            target="_blank"
            rel="noopener noreferrer"
            className="text-purple-700 hover:text-purple-900 font-semibold underline transition-colors"
          >
            Visit knowledgebase.live by Empress Ninti El Bey
          </a>
        </div>
      </div>
    </div>
  );
};

export default GameDashboard;